import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Shield, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <nav className="flex justify-between items-center mb-12">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg">
              <Shield className="h-5 w-5" />
            </div>
            <h1 className="text-xl font-bold">ServSpect</h1>
          </div>
          <Button variant="ghost" onClick={() => navigate("/")} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
        </nav>

        <Card className="p-8 md:p-12 bg-card/80 backdrop-blur">
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: December 15, 2024</p>

          <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                ServSpect ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our server monitoring platform and services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3">2.1 Account Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When you create an account, we collect:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Email address</li>
                <li>Full name</li>
                <li>Organization/tenant information</li>
                <li>Authentication credentials (securely hashed)</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">2.2 Server Metrics Data</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our monitoring agents collect technical data from your servers, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Hostname and IP addresses</li>
                <li>CPU, memory, and disk usage statistics</li>
                <li>Network traffic metrics</li>
                <li>System uptime and boot information</li>
                <li>Running processes and services</li>
                <li>Installed software and security status</li>
                <li>Hardware information (BIOS, TPM, device serial)</li>
                <li>Windows Defender and firewall status</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">2.3 Windows Event Logs</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                With your authorization, we collect Windows event log data including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Security events (login attempts, account lockouts)</li>
                <li>System events (service starts/stops, crashes)</li>
                <li>Application events</li>
                <li>PowerShell execution logs</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">2.4 Security & Audit Data</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For security and compliance purposes, we collect:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Audit logs of user actions within the platform</li>
                <li>Security incident reports</li>
                <li>Device tokens and enrollment data</li>
                <li>IP addresses of connecting devices</li>
                <li>User agent information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Providing real-time server monitoring and alerting services</li>
                <li>Displaying dashboards and generating reports</li>
                <li>Detecting security threats and anomalies</li>
                <li>Sending alerts and notifications about your infrastructure</li>
                <li>Maintaining compliance with security frameworks</li>
                <li>Improving our services and user experience</li>
                <li>Providing customer support</li>
                <li>Ensuring platform security and preventing abuse</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We retain your data according to the following policies:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Metrics data:</strong> Retained according to your configured retention policy (default: 90 days)</li>
                <li><strong>Event logs:</strong> Retained according to your configured retention policy (default: 30 days)</li>
                <li><strong>Audit logs:</strong> Retained for 1 year for compliance purposes</li>
                <li><strong>Account data:</strong> Retained as long as your account is active</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                You can configure data retention policies in your account settings. Upon account deletion, we will remove your data within 30 days, except where retention is required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We implement robust security measures to protect your data:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>All data is encrypted in transit using TLS 1.3</li>
                <li>Data at rest is encrypted using AES-256</li>
                <li>Device authentication using secure tokens</li>
                <li>Row-level security (RLS) for tenant data isolation</li>
                <li>Rate limiting and intrusion detection</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>PII detection and automatic masking capabilities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Data Sharing</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not sell your data. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Service providers:</strong> With trusted third parties who assist in operating our platform (e.g., cloud hosting providers)</li>
                <li><strong>Legal requirements:</strong> When required by law, court order, or governmental authority</li>
                <li><strong>Security incidents:</strong> To protect against fraud, security threats, or illegal activities</li>
                <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Objection:</strong> Object to certain processing of your data</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                To exercise these rights, please contact us at privacy@servspect.com.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use essential cookies for authentication and session management. We do not use tracking cookies or third-party analytics cookies without your consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. International Data Transfers</h2>
              <p className="text-muted-foreground leading-relaxed">
                Your data may be processed in countries outside your jurisdiction. When we transfer data internationally, we ensure appropriate safeguards are in place, including Standard Contractual Clauses where applicable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of our services after any changes indicates your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <p className="text-foreground font-medium">ServSpect</p>
                <p className="text-muted-foreground">Email: privacy@servspect.com</p>
              </div>
            </section>
          </div>
        </Card>

        <div className="text-center mt-8 text-sm text-muted-foreground">
          © {new Date().getFullYear()} ServSpect. All rights reserved.
        </div>
      </div>
    </div>
  );
}
